import { useState, useEffect } from 'react';

interface GuestWishlist {
  id: string;
  title: string;
  description?: string;
  items: GuestWishlistItem[];
  created_at: string;
}

interface GuestWishlistItem {
  id: string;
  title: string;
  price?: number;
  image_url?: string;
  original_url: string;
  affiliate_url?: string;
  currency?: string;
  is_purchased: boolean;
  created_at: string;
}

const GUEST_WISHLISTS_KEY = 'wishful_guest_wishlists';

export const useGuestWishlists = () => {
  const [wishlists, setWishlists] = useState<GuestWishlist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load guest wishlists from localStorage
    const stored = localStorage.getItem(GUEST_WISHLISTS_KEY);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setWishlists(parsed);
      } catch (error) {
        console.error('Failed to parse guest wishlists:', error);
      }
    }
    setLoading(false);
  }, []);

  const saveToStorage = (wishlists: GuestWishlist[]) => {
    localStorage.setItem(GUEST_WISHLISTS_KEY, JSON.stringify(wishlists));
    setWishlists(wishlists);
  };

  const createWishlist = (title: string, description?: string): GuestWishlist => {
    const newWishlist: GuestWishlist = {
      id: `guest_${Date.now()}`,
      title,
      description,
      items: [],
      created_at: new Date().toISOString()
    };

    const updatedWishlists = [newWishlist, ...wishlists];
    saveToStorage(updatedWishlists);
    return newWishlist;
  };

  const addItemToWishlist = (
    wishlistId: string,
    title: string,
    originalUrl: string,
    price?: number,
    imageUrl?: string,
    affiliateUrl?: string,
    currency?: string
  ): GuestWishlistItem => {
    const newItem: GuestWishlistItem = {
      id: `item_${Date.now()}`,
      title,
      original_url: originalUrl,
      affiliate_url: affiliateUrl,
      price,
      image_url: imageUrl,
      currency: currency || 'USD',
      is_purchased: false,
      created_at: new Date().toISOString()
    };

    const updatedWishlists = wishlists.map(wishlist =>
      wishlist.id === wishlistId
        ? { ...wishlist, items: [...wishlist.items, newItem] }
        : wishlist
    );

    saveToStorage(updatedWishlists);
    return newItem;
  };

  const removeItemFromWishlist = (itemId: string, wishlistId: string) => {
    const updatedWishlists = wishlists.map(wishlist =>
      wishlist.id === wishlistId
        ? { ...wishlist, items: wishlist.items.filter(item => item.id !== itemId) }
        : wishlist
    );

    saveToStorage(updatedWishlists);
  };

  const markItemAsPurchased = (itemId: string, wishlistId: string, purchased: boolean) => {
    const updatedWishlists = wishlists.map(wishlist =>
      wishlist.id === wishlistId
        ? {
            ...wishlist,
            items: wishlist.items.map(item =>
              item.id === itemId ? { ...item, is_purchased: purchased } : item
            )
          }
        : wishlist
    );

    saveToStorage(updatedWishlists);
  };

  const deleteWishlist = (wishlistId: string) => {
    const updatedWishlists = wishlists.filter(wishlist => wishlist.id !== wishlistId);
    saveToStorage(updatedWishlists);
  };

  const getWishlistById = (wishlistId: string): GuestWishlist | undefined => {
    return wishlists.find(wishlist => wishlist.id === wishlistId);
  };

  return {
    wishlists,
    loading,
    createWishlist,
    addItemToWishlist,
    removeItemFromWishlist,
    markItemAsPurchased,
    deleteWishlist,
    getWishlistById
  };
};
